<template>
  <div>
    <DropdownMenu v-if="visibleActions.length > 0">
      <DropdownMenuTrigger as-child>
        <button
          class="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-text-xs tw-text-secondary-500 hover:tw-text-white tw-bg-white tw-border tw-border-solid tw-border-secondary-600 tw-rounded-md hover:tw-bg-secondary-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-secondary-500 focus:tw-border-secondary-500"
          type="button"
        >
          <div class="tw-flex tw-items-center tw-gap-2">
            <span class="tw-text-xs tw-font-light">
              {{ t('common.actions.bulk_actions') }}
            </span>
            <DotsVerticalIcon class="tw-w-4 tw-h-4" />
          </div>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent class="tw-w-56 !tw-border-none !tw-shadow-[0px_0px_19px_0px_#f5f5f5]">
        <DropdownMenuItem
          v-for="action in visibleActions"
          :key="action.label"
          @click="action.onClick"
        >
          <div class="tw-flex tw-items-center tw-gap-2">
            <component
              :is="action.icon"
              class="tw-w-4 tw-h-4"
              :class="action.iconClass"
              v-if="action.icon"
            />
            <span>{{ action.label }}</span>
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>

    <!-- Confirmation Modal -->
    <ConfirmationModal
      v-model="showConfirmationModal"
      :title="t('common.forms.confirmation').toUpperCase()"
      :message="confirmationMessage"
      :type="confirmationModalType"
      :confirm-text="t('common.forms.confirm')"
      :cancel-text="t('common.forms.cancel')"
      @confirm="onConfirmAction"
      @cancel="closeConfirmationModal"
    />

    <!-- Request clarification modal -->
    <GenericModal
      v-model="showRequestClarificationModal"
      :title="t('orders.titles.confirm_clarification')"
    >
      <template #body>
        <div class="tw-mb-4">
          <Textarea
            v-model="clarificationComment"
            class="tw-border tw-w-full tw-px-3 tw-py-2 tw-rounded tw-resize-none"
            :placeholder="t('orders.titles.message_hint')"
            rows="6"
          ></Textarea>
        </div>
      </template>

      <template #footer>
        <div class="tw-flex tw-justify-end tw-gap-2 tw-items-center">
          <Button
            class="tw-bg-white tw-border-solid tw-border-2 tw-border-secondary-500 tw-text-secondary-500 hover:tw-bg-secondary-500 hover:tw-text-white tw-px-2 tw-py-2 tw-rounded-md tw-text-sm tw-flex tw-gap-2 tw-transition-colors"
            type="button"
            @click="onCloseRequestClarificationModal"
          >
            {{ t('common.forms.cancel') }}
          </Button>
          <Button
            class="tw-bg-secondary-500 tw-border-2 tw-border-secondary-500 tw-text-white hover:tw-bg-secondary-600 tw-border-solid tw-px-2 tw-py-2 tw-rounded-md tw-text-sm tw-flex tw-gap-2 tw-transition-colors"
            :class="{
              'tw-opacity-50 !tw-cursor-not-allowed': disableActions || !clarificationComment,
            }"
            type="button"
            @click="performClarificationAction"
            :disabled="disableActions || !clarificationComment"
          >
            {{ t('requests.actions.request_clarification') }}
          </Button>
        </div>
      </template>
    </GenericModal>

    <!-- Export modal -->
    <GenericModal
      v-model="showExportModal"
      :title="t('common.actions.export')"
      extra-class="tw-min-w-[800px]"
    >
      <template #body>
        <export-form
          :fields="exportFields"
          :allow-pdf="isPdfExportEnabled"
          @export="exportAllOrders"
          @cancel="closeExportModal"
        ></export-form>
      </template>
    </GenericModal>
  </div>
</template>

<script setup lang="ts">
import { useApiUtils } from '@/_helpers/composables/api-utils.composable';
import ApproveIcon from '@/assets/icons/Approve.svg';
import CheckTextIcon from '@/assets/icons/CheckText.svg';
import CloseIcon from '@/assets/icons/Close.svg';
import DotsVerticalIcon from '@/assets/icons/DotsVertical.svg';
import FileExportIcon from '@/assets/icons/FileExport.svg';
import ForwardIcon from '@/assets/icons/Forward.svg';
import QuestionIcon from '@/assets/icons/Question.svg';
import { Ban, PackageCheck, PackageX, RotateCcw } from 'lucide-vue-next';
import { storeToRefs } from 'pinia';
import { computed, ref, shallowRef } from 'vue';
import { useI18n } from 'vue-i18n';

import DeleteIcon from '@/assets/icons/Trash.svg';
import { ConfirmationModal, GenericModal } from '@/components/shared';
import { Button } from '@/components/ui/button';
import { useToast } from 'vue-toast-notification';

import ExportForm from '@/components/shared/export/export-form.vue';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Textarea } from '@/components/ui/textarea';
import { useCustomerStore } from '@/stores/customer.store';
import { useOrderStore } from '@/stores/order.store';
import { useUserStore } from '@/stores/user.store';
import { OrderStatus } from '@/views/Orders/types';
import { UserRole } from '@/views/Users/types';

const { currentUser } = storeToRefs(useUserStore());
const { handleApiError } = useApiUtils();
const { t } = useI18n();
const toast = useToast();

const { ordersBulkAction, exportOrders } = useOrderStore();

// Action type enum
enum ActionType {
  DELETE = 'delete',
  EDIT = 'edit',
  APPROVE = 'approve',
  REJECT = 'reject',
  CLARIFICATION = 'clarification',
  MARK_CLARIFICATIONS_COMPLETED = 'mark_clarifications_completed',
  SUBMIT = 'submit',
  MARK_FULFILLED = 'mark_fulfilled',
  MARK_UNFULFILLED = 'mark_unfulfilled',
  MARK_CANCELED = 'mark_canceled',
  MARK_UNCANCELED = 'mark_uncanceled',
}

const customerStore = useCustomerStore();
const disableActions = computed(() => customerStore.shouldDisableCustomerActions());

const showConfirmationModal = ref(false);
const showExportModal = ref(false);
const confirmationMessage = ref('');
const currentActionType = ref<ActionType | null>(null);

// Request clarification modal
const showRequestClarificationModal = ref(false);
const clarificationComment = ref('');
const confirmationModalType = ref<'warning' | 'danger' | 'info' | 'success'>('warning');

const props = defineProps<{
  selectedOrders: any[];
}>();

const emit = defineEmits<{
  clearSelection;
  reloadTable;
}>();

const performSubmitAction = async () => {
  const orderIds = props.selectedOrders.map(order => order.id);
  await handleApiError(async () => {
    await ordersBulkAction('bulk_submit', orderIds);
    emit('clearSelection');
    emit('reloadTable');
  }, t('orders.titles.successful_submit'));
};

const performApproveAction = async () => {
  const orderIds = props.selectedOrders.map(order => order.id);
  await handleApiError(async () => {
    await ordersBulkAction('bulk_approve', orderIds);
    emit('clearSelection');
    emit('reloadTable');
  }, t('orders.titles.successful_approval'));
};

const performRejectAction = async () => {
  const orderIds = props.selectedOrders.map(order => order.id);
  await handleApiError(async () => {
    await ordersBulkAction('bulk_reject', orderIds);
    emit('clearSelection');
    emit('reloadTable');
  }, t('orders.titles.successful_rejection'));
};

const performDeleteAction = async () => {
  const orderIds = props.selectedOrders.map(order => order.id);
  await handleApiError(async () => {
    await ordersBulkAction('bulk_delete', orderIds);
    emit('clearSelection');
    emit('reloadTable');
  }, t('orders.titles.successful_delete'));
};

const performClarificationAction = async () => {
  const orderIds = props.selectedOrders.map(order => order.id);
  await handleApiError(async () => {
    await ordersBulkAction('bulk_request_clarification', orderIds, clarificationComment.value);
    emit('clearSelection');
    emit('reloadTable');
    onCloseRequestClarificationModal();
    clarificationComment.value = '';
  }, t('orders.titles.successful_clarification'));
};

const onCloseRequestClarificationModal = () => {
  showRequestClarificationModal.value = false;
  clarificationComment.value = '';
};

const openRequestClarificationModal = () => {
  currentActionType.value = ActionType.CLARIFICATION;
  showRequestClarificationModal.value = true;
};

const respondToClarification = async () => {
  const orderIds = props.selectedOrders.map(order => order.id);
  await handleApiError(async () => {
    await ordersBulkAction('bulk_respond_to_clarification_request', orderIds);
    emit('clearSelection');
    emit('reloadTable');
  }, t('orders.titles.successful_clarification_response'));
};

const performMarkFulfilledAction = async () => {
  const orderIds = props.selectedOrders.map(order => order.id);
  await handleApiError(async () => {
    await ordersBulkAction('bulk_update_fulfilled', orderIds, undefined, {
      fulfilled: true,
    });
    emit('clearSelection');
    emit('reloadTable');
  }, t('orders.titles.successful_mark_fulfilled'));
};
const performMarkUnfulfilledAction = async () => {
  const orderIds = props.selectedOrders.map(order => order.id);
  await handleApiError(async () => {
    await ordersBulkAction('bulk_update_fulfilled', orderIds, undefined, {
      fulfilled: false,
    });
    emit('clearSelection');
    emit('reloadTable');
  }, t('orders.titles.successful_mark_unfulfilled'));
};

const performMarkCanceledAction = async () => {
  const orderIds = props.selectedOrders.map(order => order.id);
  await handleApiError(async () => {
    await ordersBulkAction('bulk_update_canceled', orderIds, undefined, {
      canceled: true,
    });
    emit('clearSelection');
    emit('reloadTable');
  }, t('orders.titles.successful_mark_canceled'));
};

const performMarkUncanceledAction = async () => {
  const orderIds = props.selectedOrders.map(order => order.id);
  await handleApiError(async () => {
    await ordersBulkAction('bulk_update_canceled', orderIds, undefined, {
      canceled: false,
    });
    emit('clearSelection');
    emit('reloadTable');
  }, t('orders.titles.successful_mark_uncanceled'));
};

const actionMap = {
  [ActionType.SUBMIT]: performSubmitAction,
  [ActionType.APPROVE]: performApproveAction,
  [ActionType.REJECT]: performRejectAction,
  [ActionType.DELETE]: performDeleteAction,
  [ActionType.CLARIFICATION]: performClarificationAction,
  [ActionType.MARK_CLARIFICATIONS_COMPLETED]: respondToClarification,
  [ActionType.MARK_FULFILLED]: performMarkFulfilledAction,
  [ActionType.MARK_UNFULFILLED]: performMarkUnfulfilledAction,
  [ActionType.MARK_CANCELED]: performMarkCanceledAction,
  [ActionType.MARK_UNCANCELED]: performMarkUncanceledAction,
};

const actionTypeToMessageMap = {
  [ActionType.SUBMIT]: t('orders.titles.confirm_forward'),
  [ActionType.APPROVE]: t('orders.titles.confirm_approval'),
  [ActionType.REJECT]: t('orders.titles.confirm_rejection'),
  [ActionType.DELETE]: t('orders.titles.confirm_delete'),
  [ActionType.CLARIFICATION]: t('orders.titles.confirm_mark_clarifications_completed'),
  [ActionType.MARK_CLARIFICATIONS_COMPLETED]: t(
    'orders.titles.confirm_mark_clarifications_completed'
  ),
  [ActionType.MARK_FULFILLED]: t('orders.titles.confirm_mark_fulfilled'),
  [ActionType.MARK_UNFULFILLED]: t('orders.titles.confirm_mark_unfulfilled'),
  [ActionType.MARK_CANCELED]: t('orders.titles.confirm_mark_canceled'),
  [ActionType.MARK_UNCANCELED]: t('orders.titles.confirm_mark_uncanceled'),
};

const actionTypeToModalTypeMap = {
  [ActionType.SUBMIT]: 'info',
  [ActionType.APPROVE]: 'success',
  [ActionType.REJECT]: 'warning',
  [ActionType.DELETE]: 'danger',
};

const openConfirmationModal = (actionType: ActionType) => {
  confirmationModalType.value = actionTypeToModalTypeMap[actionType];
  currentActionType.value = actionType;
  confirmationMessage.value = actionTypeToMessageMap[actionType];
  showConfirmationModal.value = true;
};

const openExportModal = () => {
  showExportModal.value = true;
};

const closeExportModal = () => {
  showExportModal.value = false;
};

const onConfirmAction = async () => {
  if (currentActionType.value === null) return;
  await actionMap[currentActionType.value]();
};

const closeConfirmationModal = () => {
  showConfirmationModal.value = false;
};

const showBulkSubmitDeleteBtn = () => {
  // make sure all selected orders are drafts
  return (
    props.selectedOrders.length &&
    props.selectedOrders.filter((item: any) => {
      return !item.recurring_instruction && item.status === OrderStatus.DRAFT;
    }).length == props.selectedOrders.length
  );
};

const showBulkMarkFulfilledBtn = () => {
  if (
    !(
      currentUser.value?.user_role.includes(UserRole.ADMIN) ||
      currentUser.value?.user_role.includes(UserRole.PURCHASER) ||
      currentUser.value?.user_role.includes(UserRole.ORDER_CLEARER)
    )
  ) {
    return false;
  }
  return (
    props.selectedOrders.length &&
    props.selectedOrders.filter((item: any) => {
      return item.status === OrderStatus.APPROVED;
    }).length == props.selectedOrders.length
  );
};

const hasCanceledRoleAccess = () =>
  currentUser.value?.user_role.includes(UserRole.ADMIN) ||
  currentUser.value?.user_role.includes(UserRole.APPROVER) ||
  currentUser.value?.user_role.includes(UserRole.ORDER_CLEARER);

const allSelectedAreApproved = () =>
  !!props.selectedOrders.length &&
  props.selectedOrders.every((item: any) => item.status === OrderStatus.APPROVED);

const showBulkMarkCanceledBtn = () => {
  if (!hasCanceledRoleAccess()) return false;
  return allSelectedAreApproved() && props.selectedOrders.every((item: any) => !item.canceled);
};

const showBulkMarkUncanceledBtn = () => {
  if (!hasCanceledRoleAccess()) return false;
  return allSelectedAreApproved() && props.selectedOrders.every((item: any) => item.canceled);
};

const exportFields = [
  { label: t('orders.titles.order_no'), value: 'order_number' },
  { label: t('orders.titles.supplier'), value: 'supplier_name' },
  { label: t('orders.titles.supplier_no'), value: 'supplier_number' },
  { label: t('orders.titles.doc_no_internal'), value: 'document_no_internal' },
  { label: t('orders.titles.project_no'), value: 'project_no' },
  { label: t('orders.titles.current_balance'), value: 'balance' },
  { label: t('orders.titles.amount_gross'), value: 'amount' },
  { label: t('orders.titles.created_on'), value: 'created_on' },
  { label: t('orders.titles.created_by'), value: 'created_by' },
  { label: t('orders.titles.released_by'), value: 'released_by' },
  { label: t('orders.titles.service_date'), value: 'service_date' },
  { label: t('requests.titles.range_in_months'), value: 'range_in_months' },
];

const isPdfExportEnabled = computed(() => {
  return (
    props.selectedOrders.length > 0 &&
    props.selectedOrders.every((order: any) => order.status === 'APPROVED')
  );
});

const exportAllOrders = async (data: any) => {
  showExportModal.value = false;
  let fileFormat = data.exportFormat;
  let fields = data.fields;
  const selectedOrdersIds = props.selectedOrders.map((order: any) => order.id);

  try {
    const data = await exportOrders(selectedOrdersIds, fileFormat, fields);
    toast.open({
      message: t('orders.titles.successful_export'),
      type: 'success',
    });
    // open file url in new tab
    window.open(data.file_url, '_blank');
  } catch (error: any) {
    toast.open({
      message: error.response.data.errors[0].detail,
      type: 'error',
    });
  }
};

const showBulkRequestClarificationBtn = () => {
  return (
    props.selectedOrders.length &&
    props.selectedOrders.filter((item: any) => {
      return (
        !item.recurring_instruction &&
        [OrderStatus.PENDING_APPROVAL, OrderStatus.CLEARING].includes(item.status_display) &&
        !item.is_clarification_requested &&
        (currentUser.value?.user_role.includes(UserRole.ORDER_CLEARER) ||
          currentUser.value?.user_role.includes(UserRole.APPROVER) ||
          currentUser.value?.user_role.includes(UserRole.ADMIN))
      );
    }).length == props.selectedOrders.length
  );
};

const showBulkApproveBtn = () => {
  if (currentUser.value?.user_role.includes(UserRole.ADMIN)) {
    return (
      props.selectedOrders.length &&
      props.selectedOrders.filter((item: any) => {
        return !item.recurring_instruction && item.status_display === OrderStatus.CLEARING;
      }).length === props.selectedOrders.length
    );
  }
  if (currentUser.value?.user_role.includes(UserRole.APPROVER)) {
    return (
      props.selectedOrders.length &&
      props.selectedOrders.filter((item: any) => {
        // this only happens when all clearers have cleared the order, or at least
        // one has cleared and the order is not in strict mode
        return !item.recurring_instruction && item.status_display === OrderStatus.PENDING_APPROVAL;
      }).length == props.selectedOrders.length
    );
  }
  if (currentUser.value?.user_role.includes(UserRole.ORDER_CLEARER)) {
    return (
      props.selectedOrders.length &&
      props.selectedOrders.filter((item: any) => {
        return !item.recurring_instruction && item.status_display === OrderStatus.PENDING_APPROVAL;
      }).length == props.selectedOrders.length
    );
  }
  return false;
};

const showBulkRejectBtn = () => {
  return (
    (currentUser.value?.user_role.includes(UserRole.ADMIN) ||
      currentUser.value?.user_role.includes(UserRole.APPROVER) ||
      currentUser.value?.user_role.includes(UserRole.ORDER_CLEARER)) &&
    props.selectedOrders.length &&
    props.selectedOrders.filter((item: any) => {
      return (
        !item.recurring_instruction &&
        (item.status_display == OrderStatus.CLEARING ||
          item.status_display == OrderStatus.PENDING_APPROVAL)
      );
    }).length === props.selectedOrders.length
  );
};

const showBulkMarkClarificationsCompletedBtn = () => {
  return (
    props.selectedOrders.filter((item: any) => {
      return (
        !item.recurring_instruction &&
        item.is_clarification_requested &&
        (currentUser.value?.id === item.created_by ||
          currentUser.value?.id === item.requested_clarification_by)
      );
    }).length === props.selectedOrders.length
  );
};

const showBulkExport = () => {
  return (
    props.selectedOrders.length > 0 &&
    props.selectedOrders.filter((order: any) => {
      return order.status === OrderStatus.DRAFT;
    }).length === 0
  );
};

const actions = shallowRef<any[]>([
  {
    label: t('common.forms.submit'),
    icon: ForwardIcon,
    iconClass: '!tw-text-blue-500',
    onClick: () => {
      openConfirmationModal(ActionType.SUBMIT);
    },
    show: () => {
      return showBulkSubmitDeleteBtn();
    },
    disabled: disableActions.value,
    tooltip: disableActions.value
      ? {
          theme: 'inocu-tooltip',
          content: t('common.message.customer_archived_tooltip'),
        }
      : null,
  },
  {
    label: t('requests.actions.approve'),
    icon: ApproveIcon,
    iconClass: 'tw-text-green-500',
    onClick: () => {
      openConfirmationModal(ActionType.APPROVE);
    },
    show: () => {
      return showBulkApproveBtn();
    },
    disabled: disableActions.value,
    tooltip: disableActions.value
      ? {
          theme: 'inocu-tooltip',
          content: t('common.message.customer_archived_tooltip'),
        }
      : null,
  },
  {
    label: t('requests.actions.request_clarification'),
    icon: QuestionIcon,
    iconClass: 'tw-text-yellow-500',
    onClick: () => {
      openRequestClarificationModal();
    },
    show: () => {
      return showBulkRequestClarificationBtn();
    },
    disabled: disableActions.value,
    tooltip: disableActions.value
      ? {
          theme: 'inocu-tooltip',
          content: t('common.message.customer_archived_tooltip'),
        }
      : null,
  },
  {
    label: t('orders.actions.mark_clarifications_completed'),
    icon: CheckTextIcon,
    iconClass: 'tw-text-green-500',
    onClick: () => {
      openConfirmationModal(ActionType.MARK_CLARIFICATIONS_COMPLETED);
    },
    show: () => {
      return showBulkMarkClarificationsCompletedBtn();
    },
    disabled: disableActions.value,
    tooltip: disableActions.value
      ? {
          theme: 'inocu-tooltip',
          content: t('common.message.customer_archived_tooltip'),
        }
      : null,
  },
  {
    label: t('requests.actions.reject'),
    icon: CloseIcon,
    iconClass: 'tw-text-red-500',
    onClick: () => {
      openConfirmationModal(ActionType.REJECT);
    },
    show: () => {
      return showBulkRejectBtn();
    },
    disabled: disableActions.value,
    tooltip: disableActions.value
      ? {
          theme: 'inocu-tooltip',
          content: t('common.message.customer_archived_tooltip'),
        }
      : null,
  },
  {
    label: t('common.forms.delete'),
    icon: DeleteIcon,
    iconClass: 'tw-text-red-500',
    onClick: () => {
      openConfirmationModal(ActionType.DELETE);
    },
    show: () => {
      return showBulkSubmitDeleteBtn();
    },
    disabled: disableActions.value,
    tooltip: disableActions.value
      ? {
          theme: 'inocu-tooltip',
          content: t('common.message.customer_archived_tooltip'),
        }
      : null,
  },
  {
    label: t('orders.actions.mark_fulfilled'),
    icon: PackageCheck,
    iconClass: 'tw-text-emerald-600',
    onClick: () => {
      openConfirmationModal(ActionType.MARK_FULFILLED);
    },
    show: () => {
      return showBulkMarkFulfilledBtn();
    },
    disabled: disableActions.value,
    tooltip: disableActions.value
      ? {
          theme: 'inocu-tooltip',
          content: t('common.message.customer_archived_tooltip'),
        }
      : null,
  },
  {
    label: t('orders.actions.mark_unfulfilled'),
    icon: PackageX,
    iconClass: 'tw-text-red-500',
    onClick: () => {
      openConfirmationModal(ActionType.MARK_UNFULFILLED);
    },
    show: () => {
      return showBulkMarkFulfilledBtn();
    },
    disabled: disableActions.value,
    tooltip: disableActions.value
      ? {
          theme: 'inocu-tooltip',
          content: t('common.message.customer_archived_tooltip'),
        }
      : null,
  },
  {
    label: t('orders.actions.mark_canceled'),
    icon: Ban,
    iconClass: 'tw-text-red-500',
    onClick: () => {
      openConfirmationModal(ActionType.MARK_CANCELED);
    },
    show: () => {
      return showBulkMarkCanceledBtn();
    },
    disabled: disableActions.value,
    tooltip: disableActions.value
      ? {
          theme: 'inocu-tooltip',
          content: t('common.message.customer_archived_tooltip'),
        }
      : null,
  },
  {
    label: t('orders.actions.mark_uncanceled'),
    icon: RotateCcw,
    iconClass: 'tw-text-blue-600',
    onClick: () => {
      openConfirmationModal(ActionType.MARK_UNCANCELED);
    },
    show: () => {
      return showBulkMarkUncanceledBtn();
    },
    disabled: disableActions.value,
    tooltip: disableActions.value
      ? {
          theme: 'inocu-tooltip',
          content: t('common.message.customer_archived_tooltip'),
        }
      : null,
  },
  {
    label: t('common.actions.export'),
    icon: FileExportIcon,
    iconClass: 'tw-text-blue-500',
    onClick: () => {
      openExportModal();
    },
    show: () => {
      return showBulkExport();
    },
  },
]);

const visibleActions = computed(() => {
  return actions.value.filter(action => action.show());
});
</script>
